import React, { PropsWithChildren, useState } from 'react'
import classNames from 'classnames'
import { IconButton } from '../../common/IconButton/IconButton'

type AccordionProps = PropsWithChildren & {
    name: string
    openItems: string[] // Array of currently open item names
    onToggle: (name: string) => void // Callback to handle toggling an item
    toggleWrapperClassname?: string
}

type AccordionComponent = {
    ItemToggle: React.FC<AccordionItemToggleProps>
    ItemContent: React.FC<AccordionItemContentProps>
}

const Accordion: React.FC<AccordionProps> & AccordionComponent = (props) => {
    return (
        <div data-testid={props.name}>
            <AccordionChildren {...props} />
        </div>
    )
}

const AccordionChildren: React.FC<AccordionProps> = (props: AccordionProps) => {
    const { children, openItems, onToggle } = props

    const toggleWrapperClassname = classNames(
        'flex w-full items-center gap-2',
        props.toggleWrapperClassname
    )

    return React.Children.map(children, (child) => {
        if (
            React.isValidElement<PropsWithChildren>(child) &&
            child.type === React.Fragment
        ) {
            return (
                <AccordionChildren
                    {...{ ...props, children: child.props.children }}
                />
            )
        }

        if (
            React.isValidElement<AccordionItemToggleProps>(child) &&
            child.type === Accordion.ItemToggle
        ) {
            const isOpen = openItems.includes(child.props.name)

            return (
                <div
                    className={toggleWrapperClassname}
                    onClick={() => {
                        onToggle(child.props.name)
                    }}
                >
                    {child.props.children}
                    <IconButton
                        name={`${child.props.name}-toggle`}
                        aria-expanded={isOpen}
                        className="ml-auto"
                        icon={isOpen ? 'expand_less' : 'expand_more'}
                        size="medium"
                        variant="ghostSecondary"
                    />
                </div>
            )
        }

        if (
            React.isValidElement<AccordionItemContentProps>(child) &&
            child.type === Accordion.ItemContent
        ) {
            const isOpen = openItems.includes(child.props.name)
            const wrapperClassName = classNames(
                'grid grid-rows-[0fr] transition-all',
                {
                    'grid-rows-[1fr]': isOpen,
                    'opacity-0': !isOpen,
                }
            )
            return (
                <div className={wrapperClassName} aria-hidden={!isOpen}>
                    <div
                        className="overflow-hidden"
                        data-testid={`${child.props.name}-content`}
                    >
                        {child.props.children}
                    </div>
                </div>
            )
        }

        return null
    })
}

type AccordionItemToggleProps = PropsWithChildren & {
    name: string
    onClick?: () => void
}

const AccordionItemToggle: React.FC<AccordionItemToggleProps> = ({
    children,
    onClick,
}) => {
    return <div onClick={onClick}>{children}</div>
}

type AccordionItemContentProps = PropsWithChildren & {
    name: string
}

const AccordionItemContent: React.FC<AccordionItemContentProps> = ({
    children,
}) => {
    return <div>{children}</div>
}

type AccordionUncontrolledProps = PropsWithChildren & {
    name: string
    singleItemOpen?: boolean // If true, only one item can be open at a time
    openItems?: string[] // initial state
    toggleWrapperClassname?: string
}

export const AccordionUncontrolled: React.FC<AccordionUncontrolledProps> &
    AccordionComponent = (props) => {
    const {
        children,
        name,
        singleItemOpen = false,
        toggleWrapperClassname,
    } = props
    const [openItems, setOpenItems] = useState<string[]>(props.openItems ?? [])

    const handleToggle = (name: string) => {
        setOpenItems((prev) => {
            if (singleItemOpen) {
                return prev.includes(name) ? [] : [name] // Only allow one item open
            } else {
                return prev.includes(name)
                    ? prev.filter((item) => item !== name) // Close item
                    : [...prev, name] // Open item
            }
        })
    }

    return (
        <Accordion
            name={name}
            openItems={openItems}
            onToggle={handleToggle}
            toggleWrapperClassname={toggleWrapperClassname}
        >
            {children}
        </Accordion>
    )
}

// Assign static components to Accordion
Accordion.ItemToggle = AccordionItemToggle
Accordion.ItemContent = AccordionItemContent

// Assign static components to AccordionUncontrolled
AccordionUncontrolled.ItemToggle = AccordionItemToggle
AccordionUncontrolled.ItemContent = AccordionItemContent

export { Accordion }
